import { validateWebmHeader } from "./webmHeader";

// EN-1217: WebKit reclaims the backing file for Blobs held in IndexedDB while leaving the
// record and the Blob handle in place — the handle keeps reporting its ORIGINAL .size, so
// every size-based integrity check on the recording pipeline (getRecordings' bytesUploaded
// vs audioData.size, TUS's uploadSize: audioFile.size, the picker's size-ceiling check) passes
// on a hollow blob. The only way to detect this is to actually read bytes back and see whether
// what comes back matches what was asked for.
//
// Scan only the head of the blob (not the whole file) — hour-long recordings can be 100+ MB;
// reading the whole thing into the JS heap just to check it's alive could OOM a tablet (same
// concern as EN-719's WEBM_HEADER_SCAN_BYTES in webmHeader.ts).
export const AUDIO_INTEGRITY_SCAN_BYTES = 64 * 1024;

// RIFF: WAV's container magic, 4 ASCII bytes at offset 0. Safari/WebKit's MediaRecorder falls
// back to audio/wav on iOS when audio/webm isn't supported (see useAudioRecorder's
// recoverIfPrefixed), so this is the container most exposed to the WebKit blob-death bug.
const RIFF_MAGIC = [0x52, 0x49, 0x46, 0x46]; // "RIFF"

// MP4/M4A: the `ftyp` (file-type) box's 4-byte type code. Unlike RIFF/EBML, ftyp isn't
// guaranteed at offset 0 -- an MP4 can legally start with a small "free"/"skip" padding box
// first -- but per ISO/IEC 14496-12 it "must be placed as early as possible in the file", and
// MediaRecorder-emitted blobs follow that convention. A box's type code sits 4 bytes into the
// box (after its own 4-byte size field), so searching a small window near the start (not parsing
// the full box tree) is enough to plausibility-check it, the same spirit as the RIFF/EBML checks.
const FTYP_MAGIC = [0x66, 0x74, 0x79, 0x70]; // "ftyp"
const FTYP_SEARCH_WINDOW_BYTES = 32;

// Both this module and webmHeader.ts cap their scan window at 64KiB for the same OOM-avoidance
// reason (see comment above); if they ever drift, the smaller one silently wins since `bytes`
// here is only ever as long as AUDIO_INTEGRITY_SCAN_BYTES allowed. Expected to stay in sync —
// confirmed equal as of this change (WEBM_HEADER_SCAN_BYTES === AUDIO_INTEGRITY_SCAN_BYTES).

// prefixHex (below) leaves the app to Sentry, a third-party SaaS, on every dead-blob event — and
// unlike a fixed magic check, these bytes can be genuine (if partial) recorded audio content: for
// read_truncated they're exactly what was actually read off the blob, and for a magic-mismatch
// case the full-length read may just be a mislabeled-but-real file rather than garbage. In a
// clinical recording pipeline that's a data-minimization concern even when the content is usually
// silence or noise. 8 bytes is enough to confirm/deny a known 4-byte container magic (RIFF/EBML)
// plus a byte or two of the header context right after it, and to recognize a recurring known
// pattern (e.g. checkStorage()'s own "test" probe blob is exactly 4 bytes) -- nowhere near enough
// to reconstruct anything resembling speech.
const SENTRY_PREFIX_BYTES = 8;

export type AudioIntegrityResult =
    | { kind: "valid" }
    | {
          kind: "dead";
          reason:
              | "read_failed"
              | "read_truncated"
              | "unrecognized_riff_container"
              | "unrecognized_webm_container"
              | "unrecognized_mp4_container";
          requestedBytes: number;
          readBytes: number;
          prefixHex: string;
      };

const toHex = (bytes: Uint8Array, n: number): string => {
    const len = Math.min(n, bytes.length);
    let out = "";
    for (let i = 0; i < len; i++) {
        out += bytes[i].toString(16).padStart(2, "0");
    }
    return out;
};

const startsWithRiffMagic = (bytes: Uint8Array): boolean => RIFF_MAGIC.every((byte, i) => bytes[i] === byte);

const containsFtypMagicNearStart = (bytes: Uint8Array): boolean => {
    const stop = Math.min(bytes.length - FTYP_MAGIC.length, FTYP_SEARCH_WINDOW_BYTES - FTYP_MAGIC.length);
    for (let i = 0; i <= stop; i++) {
        if (FTYP_MAGIC.every((byte, j) => bytes[i + j] === byte)) return true;
    }
    return false;
};

/**
 * Reads the head of `blob` and reports whether the bytes that actually come back look like a
 * live recording, instead of trusting `blob.size` (which a WebKit blob-death event leaves
 * stale — see EN-1217). Two independent signals:
 *   1. the read must return as many bytes as requested (a dead blob's backing file was
 *      reclaimed, so a read comes back short, or throws);
 *   2. if the blob claims to be a WAV file (audio/wav / audio/x-wav), the bytes read must
 *      actually start with the RIFF magic — this catches WebKit returning the FULL requested
 *      byte count but with garbage content (the "test" 4-byte husk the ticket found on disk);
 *      the same check applies to audio/webm (the default non-Safari recording container) via
 *      EN-719's validateWebmHeader (webmHeader.ts), and to audio/mp4 / audio/x-m4a via a small
 *      near-start search for the ftyp box's type code (see containsFtypMagicNearStart) — not a
 *      full box-tree parser, since a garbage/zero-filled dead blob is vanishingly unlikely to
 *      contain "ftyp" by chance in a 32-byte window near the start.
 */
export async function verifyAudioBlobIntegrity(blob: Blob): Promise<AudioIntegrityResult> {
    const requestedBytes = Math.min(blob.size, AUDIO_INTEGRITY_SCAN_BYTES);
    if (requestedBytes === 0) {
        // Empty blobs are caught by the pre-existing `.size > 0` gates elsewhere; not this
        // module's concern.
        return { kind: "valid" };
    }

    let bytes: Uint8Array;
    try {
        const buf = await blob.slice(0, requestedBytes).arrayBuffer();
        bytes = new Uint8Array(buf);
    } catch {
        return { kind: "dead", reason: "read_failed", requestedBytes, readBytes: 0, prefixHex: "" };
    }

    if (bytes.byteLength < requestedBytes) {
        return {
            kind: "dead",
            reason: "read_truncated",
            requestedBytes,
            readBytes: bytes.byteLength,
            prefixHex: toHex(bytes, SENTRY_PREFIX_BYTES),
        };
    }

    // Strip a codec parameter (e.g. "audio/webm;codecs=opus", the shape Chrome/Firefox
    // MediaRecorder blobs commonly report) before comparing container types. Matching the raw
    // blob.type with exact equality would silently skip magic-byte validation for real
    // recordings whose type happens to be codec-qualified — this codebase's own
    // createAudioFromBlobs already does the same split(";")[0] when deriving a filename
    // extension, so real chunk types are known to take this shape.
    const mimeType = (blob.type || "").split(";")[0];
    if ((mimeType === "audio/wav" || mimeType === "audio/x-wav") && !startsWithRiffMagic(bytes)) {
        return {
            kind: "dead",
            reason: "unrecognized_riff_container",
            requestedBytes,
            readBytes: bytes.byteLength,
            prefixHex: toHex(bytes, SENTRY_PREFIX_BYTES),
        };
    }

    // PR review finding: same picker-MIME mismatch as the MP4 gate below. The file picker
    // accepts .webm, but a picked file's blob.type is set by the OS/browser's own file-type
    // sniffing, not this app -- Chrome/Firefox commonly report a picked .webm as video/webm
    // (webm is a general container most commonly associated with video), not the recorder's own
    // audio/webm. For a re-picked husk, File.size IS the true (few-byte) length, so the length
    // check alone can't fire either -- only the magic check catches it, and only if this gate
    // covers the shape.
    const isWebmLikeContainer = mimeType === "audio/webm" || mimeType === "audio/x-webm" || mimeType === "video/webm";
    if (isWebmLikeContainer && bytes.byteLength > 0) {
        const webmResult = validateWebmHeader(bytes);
        if (webmResult.kind === "invalid") {
            return {
                kind: "dead",
                reason: "unrecognized_webm_container",
                requestedBytes,
                readBytes: bytes.byteLength,
                prefixHex: toHex(bytes, SENTRY_PREFIX_BYTES),
            };
        }
        // "valid" or "recoverable" both mean real bytes exist — not this module's concern which.
    }

    // PR review finding: the file picker accepts .m4a, but a picked .mp4/.m4a file's blob.type
    // is set by the OS/browser's own file-type sniffing, not by this app -- and that commonly
    // reports "video/mp4" (mp4 is a general container; browsers/OSes often lack a specific
    // audio/m4a association) or "audio/m4a" (no "x-" prefix), not just the recorder's own
    // "audio/mp4"/"audio/x-m4a". The ftyp check itself is container-format-level, not
    // audio/video-specific, so applying it to a video/mp4-labeled file is correct and safe here
    // -- every caller of this module only ever hands it a file from an audio upload/recording
    // path, so a video/mp4 label reaching it is virtually always a mislabeled audio file, not an
    // actual video upload.
    const isMp4LikeContainer =
        mimeType === "audio/mp4" || mimeType === "audio/x-m4a" || mimeType === "audio/m4a" || mimeType === "video/mp4";
    if (isMp4LikeContainer && !containsFtypMagicNearStart(bytes)) {
        return {
            kind: "dead",
            reason: "unrecognized_mp4_container",
            requestedBytes,
            readBytes: bytes.byteLength,
            prefixHex: toHex(bytes, SENTRY_PREFIX_BYTES),
        };
    }

    return { kind: "valid" };
}

/**
 * The Sentry `extra` payload shape every dead-blob call site needs: the integrity module's own
 * diagnostic fields, plus whatever context (transcriptId, claimedSize, ...) that call site wants
 * to add. Centralizing this is what stops a future edit from silently dropping a field — it
 * already happened once (missing prefixHex at three of five sites, caught only by a final
 * whole-branch review, not any single-file review).
 */
export function buildDeadBlobSentryExtra(
    integrity: Extract<AudioIntegrityResult, { kind: "dead" }>,
    context: Record<string, unknown>,
): Record<string, unknown> {
    return {
        ...context,
        reason: integrity.reason,
        requestedBytes: integrity.requestedBytes,
        readBytes: integrity.readBytes,
        prefixHex: integrity.prefixHex,
    };
}
